import { useCallback, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { cx } from '@libs/classnames';
import { FUNC_NOOP } from '@libs/constants';
import { formatName } from '@libs/formatNames';
import { DragHandle } from '@ui/components/DragHandle/DragHandle';
import { DragHandleWidget } from '@ui/components/DragHandle/DragHandleWidget';
import { TableCell, TableRow } from '@ui/components/Table';
import { ThemeButton } from '@ui/components/ThemeButton';
import { AcceptIcon } from '@ui/icons/AcceptIcon';
import { RemoveIcon } from '@ui/icons/RemoveIcon';

import { ParticipantDto, RcfAttributes } from '@features/shared/api/typings';
import { useRcfAttributesControlProps } from '@features/shared/hooks/useRcfAttributesControlProps';
import { ThemeIconButton } from '@features/shared/ui/ThemeIconButton';
import { PositionInput } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/PositionInput';
import {
  SelectTeamPopover,
  TeamOptionType,
} from '@features/tournament/ui/components/TournamentParticipantsEdit/components/SelectTeamPopover';
import { RcfAttributesControl } from '@features/user/ui/RcfAttributesControl/RcfAttributesControl';

import styles from '../TournamentParticipantsEdit.module.css';

type Props = {
  className?: string;
  participant: ParticipantDto;
  num: number;
  teamOptions: TeamOptionType[];
  isSchoolEvent: boolean;
  isRcf: boolean;
  isRcfModalOpen: boolean;
  setRcfModalOpen: (userId: string | null) => void;
  onRcfAttributesChange?: (rcfAttributes: RcfAttributes) => void;
  onDelete?: (id: string) => void;
  onAccept?: (id: string) => void;
  onRestore?: (id: string) => void;
  onRemove?: (id: string) => void;
  onBan?: (id: string) => void;
  onPgnDownload?: (id: string) => void;
  onSelectTeam?: (userId: string, teamId: string | null) => void;
  onSetOrder?: (id: string, num: number) => void;
  sortDisabled?: boolean;
  showNumbers: boolean;
  disabled?: boolean;
};

export function ParticipantRow({
  className,
  participant,
  num,
  teamOptions,
  isSchoolEvent,
  isRcf,
  isRcfModalOpen,
  setRcfModalOpen,
  onRcfAttributesChange,
  onDelete,
  onAccept,
  onRestore,
  onRemove,
  onBan,
  onPgnDownload,
  onSelectTeam,
  onSetOrder,
  sortDisabled,
  disabled,
  showNumbers,
}: Props) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { setNodeRef, transition, transform, active } = useSortable({
    id: participant.userId,
    data: {
      type: 'participant',
      userId: participant.userId,
      teamId: participant.teamId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: active?.id === participant.userId ? 0 : undefined,
  };

  const handleDelete = useCallback(() => {
    onDelete?.(participant.userId);
  }, [participant, onDelete]);
  const handleAccept = useCallback(() => {
    onAccept?.(participant.userId);
  }, [participant, onAccept]);
  const handleRestore = useCallback(() => {
    onRestore?.(participant.userId);
  }, [participant, onRestore]);
  const handleRemove = useCallback(() => {
    const accepted = confirm(`Удалить участника ${formatName(participant)} из турнира?`);
    if (accepted) {
      onRemove?.(participant.userId);
    }
  }, [participant, onRemove]);
  const handleBan = useCallback(() => {
    const accepted = confirm(`Забанить участника ${formatName(participant)} в турире?`);
    if (accepted) {
      onBan?.(participant.userId);
    }
  }, [participant, onBan]);
  const handlePgnDownload = useCallback(() => {
    onPgnDownload?.(participant.userId);
  }, [participant, onPgnDownload]);
  const handleSetOrder = useCallback(
    (newOrder: number) => {
      onSetOrder?.(participant.userId, newOrder);
    },
    [onSetOrder, participant.userId],
  );
  const togglePopover = useCallback(() => {
    setIsPopoverOpen(!isPopoverOpen);
  }, [setIsPopoverOpen, isPopoverOpen]);
  const hidePopover = useCallback(() => {
    setIsPopoverOpen(false);
  }, [setIsPopoverOpen]);

  const handleSelectTeam = useCallback(
    (teamId: string | null) => {
      onSelectTeam?.(participant.userId, teamId);
    },
    [onSelectTeam, participant.userId],
  );

  const rcfProps = useRcfAttributesControlProps(participant, setRcfModalOpen);

  return (
    <TableRow
      className={cx(styles.participantRow, className, {
        [styles.disabled]: disabled,
      })}
      hoverable
      style={style}
      ref={setNodeRef}
    >
      {!sortDisabled && (
        <TableCell slim>
          <DragHandleWidget id={participant.userId} onClick={togglePopover} />

          {onSelectTeam && (
            <SelectTeamPopover
              isOpen={isPopoverOpen}
              hide={hidePopover}
              value={participant.teamId}
              teamOptions={teamOptions}
              onChange={handleSelectTeam}
            />
          )}
        </TableCell>
      )}

      {showNumbers && (
        <TableCell className={styles.numbers} top>
          <PositionInput key={num} value={num} onChange={onSetOrder ? handleSetOrder : undefined} />
        </TableCell>
      )}

      <TableCell top>
        <div className={styles.participantName}>{formatName(participant)}</div>
      </TableCell>
      <TableCell top>{participant.email}</TableCell>
      {isRcf && (
        <TableCell>
          {onRcfAttributesChange && (
            <RcfAttributesControl
              isModalOpen={isRcfModalOpen}
              onChange={onRcfAttributesChange}
              {...rcfProps}
            />
          )}
        </TableCell>
      )}

      {isSchoolEvent && (
        <TableCell left top>
          {participant.coachName}
        </TableCell>
      )}

      <TableCell slim right top>
        {participant.effectiveInitialRating}
      </TableCell>

      <TableCell top>
        <div className={styles.rowActions}>
          {onRestore && (
            <ThemeButton compact onClick={handleRestore}>
              Восстановить
            </ThemeButton>
          )}
          {onAccept && (
            <ThemeIconButton onClick={handleAccept}>
              <AcceptIcon />
            </ThemeIconButton>
          )}
          {onDelete && (
            <ThemeIconButton onClick={handleDelete}>
              <RemoveIcon />
            </ThemeIconButton>
          )}
          {onRemove && (
            <ThemeIconButton onClick={handleRemove}>
              <RemoveIcon />
            </ThemeIconButton>
          )}
          {onBan && (
            <ThemeIconButton className={styles.banButton} onClick={handleBan}>
              Бан
            </ThemeIconButton>
          )}
          {onPgnDownload && (
            <ThemeIconButton className={styles.downloadPgnButton} onClick={handlePgnDownload}>
              Скачать PGN
            </ThemeIconButton>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

type ParticipantRowPreviewProps = {
  participant: ParticipantDto;
  isRcf: boolean;
  isSchoolEvent: boolean;
};
export function ParticipantRowPreview({
  participant,
  isRcf,
  isSchoolEvent,
}: ParticipantRowPreviewProps) {
  const rcfAttributes: RcfAttributes = {
    rcfIdApproved: participant.rcfIdApproved,
    userId: participant.userId,
    rcfId: participant.rcfId,
    gender: participant.gender,
    birthdayTimestampMs: participant.birthdayTimestampMs,
  };

  return (
    <TableRow className={cx(styles.preview, styles.participantRow)} hoverable>
      <TableCell slim>
        <DragHandle className={styles.dragHandle} />
      </TableCell>

      <TableCell className={styles.numbers} top>
        {participant.teamId ? participant.numberInTeam : participant.numberInTournament}
      </TableCell>
      <TableCell top>{formatName(participant)}</TableCell>
      <TableCell top>{participant.email}</TableCell>
      {isRcf && (
        <TableCell leftGap>
          <RcfAttributesControl
            userName={formatName(participant)}
            isModalOpen={false}
            hideModal={FUNC_NOOP}
            openModal={FUNC_NOOP}
            onChange={FUNC_NOOP}
            value={rcfAttributes}
          />
        </TableCell>
      )}
      {isSchoolEvent && (
        <TableCell left top>
          {participant.coachName}
        </TableCell>
      )}
      <TableCell slim right top leftGap>
        {participant.effectiveInitialRating}
      </TableCell>

      <TableCell right top>
        <div className={styles.rowActions} />
      </TableCell>
    </TableRow>
  );
}
